import { Avatar, Card } from "@/components/ui";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { useSetPageTitle } from "@/context/PageTitleContext";
import { SocketContext } from "context/socket";
import { IEdoChat } from "@/shared/model/edo-chat.model";
import { IEdoChatMessage } from "@/shared/model/edo-chat-message.model";
import { IEdoChatParticipation } from "@/shared/model/edo-chat-participation.model";
import { EdoChatType } from "@/shared/model/enumerations/edo-chat-type.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import {
  ChatState,
  getUserParticipations,
  openChatWindow,
} from "@/shared/reducers/chat.reducer";
import { cn } from "@/shared/util/cn";
import { MessageCircle, Plus, Sparkles, Users } from "lucide-react";
import { useContext, useEffect, useMemo, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { CreateChat } from "./components/CreateChat";
import { CreateGroup } from "./components/CreateGroup";
import ChatDetailRoutes from "./pages/ChatDetail";
import Chat from "./pages/ChatDetail/chat";
import Info from "./pages/ChatDetail/info";

const MessengerOverview = ({
  onCreateGroup,
}: {
  onCreateGroup: () => void;
}) => {
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState("");
  const { account } = useAppSelector<AuthenticationState>((s) => s.authentication);
  const { participations } = useAppSelector<ChatState>((s) => s.chat);

  const filtered = useMemo(() => {
    const list = [...participations];
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter((p) => {
      const name =
        p.chat?.type === EdoChatType.GROUP ? p.chat?.title ?? "" : p.title ?? "";
      return name.toLowerCase().includes(q);
    });
  }, [participations, search]);

  const handleOpen = (p: IEdoChatParticipation) => {
    if (p.chat?.id != null) dispatch(openChatWindow(p.chat.id));
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl space-y-5">
        <Card className="surface overflow-hidden p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-mockup-border)] p-4">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <MessageCircle className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="font-heading text-base font-bold text-[var(--color-mockup-text)]">
                  Le tue conversazioni
                </h2>
                <p className="text-xs text-[var(--color-mockup-text-light)]">
                  Apri una chat per scrivere senza lasciare la pagina corrente
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onCreateGroup}
              className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-white shadow-sm transition-transform hover:scale-105 active:scale-95"
            >
              <Plus className="h-3.5 w-3.5" />
              Nuovo gruppo
            </button>
          </div>

          <div className="border-b border-[var(--color-mockup-border)] p-4">
            <CreateChat />
          </div>

          {filtered.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <p className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
                {participations.length === 0
                  ? "Non hai ancora conversazioni. Cerca un utente qui sopra per iniziare a chattare."
                  : "Nessuna chat corrisponde alla ricerca."}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--color-mockup-border)]">
              {filtered.map((p) => {
                const isGroup = p.chat?.type === EdoChatType.GROUP;
                const title = isGroup ? p.chat?.title : p.title;
                const initials =
                  title
                    ?.split(" ")
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((s) => s[0]?.toUpperCase())
                    .join("") || "?";
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => handleOpen(p)}
                      className={cn(
                        "flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-primary/5"
                      )}
                    >
                      <Avatar className="h-10 w-10 shrink-0 text-xs">
                        {isGroup ? <Users className="h-4 w-4" /> : initials}
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-heading text-sm font-bold text-[var(--color-mockup-text)]">
                          {title || "Chat"}
                        </p>
                        <p className="truncate text-xs text-[var(--color-mockup-text-light)]">
                          {isGroup ? "Gruppo" : "Conversazione diretta"}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs font-semibold text-primary">
                        Apri
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <p className="text-center text-xs text-[var(--color-mockup-text-light)]">
          Le chat si aprono come finestrelle in basso a destra. Puoi tenerne aperte fino
          a 3 contemporaneamente; le altre restano disponibili come bolle accanto.
        </p>
      </div>
    </div>
  );
};

const ChatRoutes = () => {
  const dispatch = useAppDispatch();
  useSetPageTitle("Messaggi", "Chat e gruppi");
  const socket = useContext(SocketContext);
  const [editChatDialog, setEditChatDialog] = useState<{
    open: boolean;
    entity: IEdoChat | null;
  }>({ open: false, entity: null });
  const { participations } = useAppSelector<ChatState>((state) => state.chat);

  const getParticipations = () => {
    dispatch(
      getUserParticipations({
        page: 0,
        size: 1000,
        sort: "id:ASC",
      })
    );
  };

  const handleOnMessageEvent = (_message: IEdoChatMessage) => {
    getParticipations();
  };

  const handleChatCreatedEvent = (_chat: IEdoChat) => {
    getParticipations();
  };

  useEffect(() => {
    getParticipations();
  }, []);

  useEffect(() => {
    socket.on("message", handleOnMessageEvent);
    socket.on("chat-created", handleChatCreatedEvent);
    return function cleanup() {
      socket.off("message", handleOnMessageEvent);
      socket.off("chat-created", handleChatCreatedEvent);
    };
  });

  return (
    <>
      <Routes>
        <Route
          index
          element={
            <MessengerOverview
              onCreateGroup={() =>
                setEditChatDialog({ open: true, entity: null })
              }
            />
          }
        />
        <Route path=":id" element={<ChatDetailRoutes />}>
          <Route index element={<Chat />} />
          <Route path="info" element={<Info />} />
        </Route>
      </Routes>
      <CreateGroup
        gruppo={editChatDialog.entity ?? undefined}
        open={editChatDialog.open}
        handleClose={() => setEditChatDialog({ open: false, entity: null })}
      />
    </>
  );
};

export default ChatRoutes;
